import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import {
    AcceptInvitationDto,
    AcceptResult,
    CreateInvitationDto,
    GetInvitationsQueryDto,
    Invitation,
    InvitationDisplay,
    InvitationResponse,
    InvitationType,
    PaginatedInvitationsResponse,
    SubmitEvaluationDto,
    SubmitResult,
} from '../models/invitation.models';
import { BaseService } from './base.service';

@Injectable({
  providedIn: 'root',
})
export class InvitationService extends BaseService<Invitation, Invitation> {
  constructor(protected override http: HttpClient) {
    super(http, 'invitations');
  }

  // ============= CREATE =============

  /**
   * Create a new invitation
   */
  createInvitation(dto: CreateInvitationDto): Observable<InvitationResponse> {
    const url = this.createUrl('');
    return this.http.post<InvitationResponse>(url, dto);
  }

  /**
   * Convenience method: Create group invitation (email)
   */
  createGroupInvitation(
    groupId: string,
    email: string,
    expirationDays: number = 7,
    personalMessage?: string
  ): Observable<InvitationResponse> {
    return this.createInvitation({
      type: InvitationType.GROUP,
      deliveryMethod: 'EMAIL' as any,
      groupId,
      inviteeEmail: email,
      expirationDays,
      personalMessage,
    });
  }

  /**
   * Convenience method: Create group URL invitation (link)
   */
  createGroupUrlInvitation(
    groupId: string,
    expirationDays: number = 30
  ): Observable<InvitationResponse> {
    return this.createInvitation({
      type: InvitationType.GROUP,
      deliveryMethod: 'LINK' as any,
      groupId,
      expirationDays,
    });
  }

  /**
   * Convenience method: Batch create group invitations
   */
  createBatchGroupInvitations(
    groupId: string,
    emails: string[],
    expirationDays: number = 7,
    personalMessage?: string
  ): Observable<InvitationResponse[]> {
    // Create promises for all invitations
    const invitations$ = emails.map((email) =>
      this.createGroupInvitation(groupId, email, expirationDays, personalMessage)
    );
    
    // Use forkJoin to wait for all to complete
    return new Observable((observer) => {
      Promise.all(invitations$.map((obs) => obs.toPromise()))
        .then((results) => {
          observer.next(results as InvitationResponse[]);
          observer.complete();
        })
        .catch((error) => observer.error(error));
    });
  }

  /**
   * Convenience method: Create evaluator invitation
   */
  createEvaluatorInvitation(
    email: string,
    expirationDays: number = 30,
    personalMessage?: string
  ): Observable<InvitationResponse> {
    return this.createInvitation({
      type: InvitationType.USER_RELATION_EVALUATOR,
      deliveryMethod: 'EMAIL' as any,
      inviteeEmail: email,
      expirationDays,
      personalMessage,
    });
  }

  /**
   * Convenience method: Create evaluated invitation
   */
  createEvaluatedInvitation(
    email: string,
    expirationDays: number = 30,
    personalMessage?: string
  ): Observable<InvitationResponse> {
    return this.createInvitation({
      type: InvitationType.USER_RELATION_EVALUATED,
      deliveryMethod: 'EMAIL' as any,
      inviteeEmail: email,
      expirationDays,
      personalMessage,
    });
  }

  /**
   * Convenience method: Create anonymous evaluation invitation
   */
  createAnonymousEvaluationInvitation(
    evaluationId: string,
    email: string,
    expirationDays: number = 30,
    personalMessage?: string,
    skillsLocked: boolean = true,
    allowedIndicatorIds?: string[]
  ): Observable<InvitationResponse> {
    return this.createInvitation({
      type: InvitationType.EVALUATION_ANONYMOUS,
      deliveryMethod: 'EMAIL' as any,
      evaluationId,
      inviteeEmail: email,
      expirationDays,
      personalMessage,
      metadata: {
        skillsLocked,
        allowedIndicatorIds,
        message: personalMessage
      } as any
    });
  }

  // ============= BATCH CREATE (with error handling) =============

  /**
   * Batch create evaluator relation invitations with individual error handling
   */
  createBatchEvaluatorRelationInvitations(emails: string[], expireDays = 30): Observable<any[]> {
    return forkJoin(
      emails.map(email => 
        this.createEvaluatorInvitation(email, expireDays).pipe(
          map(result => ({ email, success: true, invitation: result })),
          catchError(error => of({ email, success: false, error: error.message }))
        )
      )
    );
  }

  /**
   * Batch create evaluated relation invitations with individual error handling
   */
  createBatchEvaluatedRelationInvitations(emails: string[], expireDays = 30): Observable<any[]> {
    return forkJoin(
      emails.map(email => 
        this.createEvaluatedInvitation(email, expireDays).pipe(
          map(result => ({ email, success: true, invitation: result })),
          catchError(error => of({ email, success: false, error: error.message }))
        )
      )
    );
  }

  // ============= QUERY =============

  /**
   * Get invitations sent by current user
   */
  getSentInvitations(query?: GetInvitationsQueryDto): Observable<PaginatedInvitationsResponse> {
    const params = this.buildQueryParams(query);
    const url = this.createUrl('sent');
    return this.http.get<PaginatedInvitationsResponse>(url, { params });
  }

  /**
   * Get invitations received by current user
   */
  getReceivedInvitations(query?: GetInvitationsQueryDto): Observable<PaginatedInvitationsResponse> {
    const params = this.buildQueryParams(query);
    const url = this.createUrl('received');
    return this.http.get<PaginatedInvitationsResponse>(url, { params });
  }

  /**
   * Get specific invitation by ID
   */
  getInvitationById(id: string): Observable<Invitation> {
    return this.get<Invitation>(id);
  }

  /**
   * Validate invitation token
   */
  validateInvitation(token: string): Observable<{ valid: boolean; invitation?: Invitation }> {
    return this.getCustomType<{ valid: boolean; invitation?: Invitation }>(`validate/${token}`);
  }

  // ============= ACTIONS =============

  /**
   * Accept invitation by ID (authenticated)
   */
  acceptInvitationById(id: string, dto: AcceptInvitationDto = {}): Observable<AcceptResult> {
    const url = this.createUrl(`by-id/${id}/accept`);
    return this.http.post<AcceptResult>(url, dto);
  }

  /**
   * Accept invitation by token (public)
   */
  acceptInvitationByToken(token: string, dto: AcceptInvitationDto = {}): Observable<AcceptResult> {
    const url = this.createUrl(`accept/${token}`);
    return this.http.post<AcceptResult>(url, dto);
  }

  /**
   * Submit anonymous evaluation
   */
  submitEvaluation(dto: SubmitEvaluationDto): Observable<SubmitResult> {
    const url = this.createUrl('submit');
    return this.http.post<SubmitResult>(url, dto);
  }

  /**
   * Get evaluation submission by edit token (public)
   */
  getSubmissionByEditToken(editToken: string): Observable<any> {
    const url = this.createUrl(`manage/${editToken}`);
    return this.http.get<any>(url);
  }

  /**
   * Update evaluation submission by edit token (public)
   */
  updateSubmissionByEditToken(editToken: string, dto: SubmitEvaluationDto): Observable<any> {
    const url = this.createUrl(`manage/${editToken}`);
    return this.http.post<any>(url, dto);
  }

  /**
   * Confirm evaluator email by confirmation token (public)
   */
  confirmEvaluationEmail(token: string): Observable<any> {
    const url = this.createUrl(`confirm-email/${token}`);
    return this.http.get<any>(url);
  }

  /**
   * Cancel invitation by ID (authenticated)
   */
  cancelInvitation(id: string): Observable<{ success: boolean; message: string }> {
    const url = this.createUrl(`${id}/cancel`);
    return this.http.post<{ success: boolean; message: string }>(url, {});
  }

  /**
   * Cancel invitation by token (public)
   */
  cancelInvitationByToken(token: string): Observable<{ success: boolean; message: string }> {
    const url = this.createUrl(`cancel/${token}`);
    return this.http.post<{ success: boolean; message: string }>(url, {});
  }

  /**
   * Resend invitation email
   */
  resendInvitation(id: string): Observable<{ success: boolean; message: string }> {
    const url = this.createUrl(`${id}/resend`);
    return this.http.post<{ success: boolean; message: string }>(url, {});
  }

  /**
   * Delete invitation permanently
   */
  deleteInvitation(id: string): Observable<{ success: boolean; message: string }> {
    const url = this.createUrl(id);
    return this.http.delete<{ success: boolean; message: string }>(url);
  }

  // ============= LEGACY COMPATIBILITY =============

  /**
   * Legacy method for group invitations (for backward compatibility)
   * @deprecated Use createGroupInvitation instead
   */
  sendGroupInvitation(groupId: string, email: string, days: number): Observable<any> {
    return this.createGroupInvitation(groupId, email, days);
  }

  /**
   * Legacy method for accepting group invitations
   * @deprecated Use acceptInvitationByToken instead
   */
  acceptGroupInvitation(invitationId: string): Observable<any> {
    return this.acceptInvitationByToken(invitationId);
  }

  /**
   * Legacy method for URL group invitations
   * @deprecated Use acceptInvitationByToken instead
   */
  acceptGroupURLInvitation(invitationId: string): Observable<any> {
    return this.acceptInvitationByToken(invitationId);
  }

  /**
   * Legacy method for getting my invitations
   * @deprecated Use getReceivedInvitations instead
   */
  getMyInvitations(): Observable<any> {
    return this.getReceivedInvitations({ status: 'active' });
  }

  // ============= UTILITIES =============

  /**
   * Build HTTP query params from query DTO
   */
  private buildQueryParams(query?: GetInvitationsQueryDto): HttpParams {
    let params = new HttpParams();
    
    if (query) {
      if (query.type) params = params.set('type', query.type);
      if (query.status) params = params.set('status', query.status);
      if (query.deliveryMethod) params = params.set('deliveryMethod', query.deliveryMethod);
      if (query.page) params = params.set('page', query.page.toString());
      if (query.limit) params = params.set('limit', query.limit.toString());
    }
    
    return params;
  }

  /**
   * Transform invitation to display model with computed properties
   */
  toDisplayModel(invitation: Invitation): InvitationDisplay {
    const now = new Date();
    const expireDate = new Date(invitation.expireDate);
    const isExpired = expireDate < now;
    const isActive = invitation.isActive && !invitation.isUsed && !isExpired;

    return {
      ...invitation,
      statusLabel: this.getStatusLabel(invitation, isExpired),
      statusColor: this.getStatusColor(invitation, isExpired),
      typeLabel: this.getTypeLabel(invitation.type),
      actionLabel: this.getActionLabel(invitation.type),
      canResend: isActive && invitation.deliveryMethod === 'EMAIL',
      canCancel: isActive,
    };
  }

  private getStatusLabel(invitation: Invitation, isExpired: boolean): string {
    if (invitation.isUsed) return 'Accepted';
    if (!invitation.isActive) return 'Cancelled';
    if (isExpired) return 'Expired';
    return 'Pending';
  }

  private getStatusColor(invitation: Invitation, isExpired: boolean): string {
    if (invitation.isUsed) return 'green';
    if (!invitation.isActive) return 'gray';
    if (isExpired) return 'red';
    return 'yellow';
  }

  private getTypeLabel(type: InvitationType): string {
    const labels = {
      [InvitationType.GROUP]: 'Group Invitation',
      [InvitationType.USER_RELATION_EVALUATOR]: 'Evaluator Request',
      [InvitationType.USER_RELATION_EVALUATED]: 'Evaluation Request',
      [InvitationType.EVALUATION_ANONYMOUS]: 'Anonymous Evaluation',
    };
    return labels[type] || type;
  }

  private getActionLabel(type: InvitationType): string {
    const actions = {
      [InvitationType.GROUP]: 'Join Group',
      [InvitationType.USER_RELATION_EVALUATOR]: 'Accept Request',
      [InvitationType.USER_RELATION_EVALUATED]: 'Accept Request',
      [InvitationType.EVALUATION_ANONYMOUS]: 'Complete Evaluation',
    };
    return actions[type] || 'View';
  }

  /**
   * Generate frontend invitation URL
   */
  generateInvitationUrl(invitation: Invitation): string {
    const baseUrl = window.location.origin;
    const paths = {
      [InvitationType.GROUP]: `/invitations/group/${invitation.randomString}`,
      [InvitationType.USER_RELATION_EVALUATOR]: `/invitations/evaluator/${invitation.randomString}`,
      [InvitationType.USER_RELATION_EVALUATED]: `/invitations/evaluated/${invitation.randomString}`,
      [InvitationType.EVALUATION_ANONYMOUS]: `/evaluate/${invitation.randomString}`,
    };
    return `${baseUrl}${paths[invitation.type] || `/invitations/${invitation.randomString}`}`;
  }
}

